/**
 * Portal Manta · Card2Importar
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { M, F, STATUS } from '../../tokens/theme.js';
import { extKm } from '../../globals.js';
import { importarExcelKpis } from '../agentes/importarExcelKpis.jsx';
import { SharePointPicker } from './SharePointPicker.jsx';
import { importarJSONkpisAvancado } from '../agentes/importarJSONkpisAvancado.jsx';
import { CalloutM } from '../shared/ArtefatoHelpers.jsx';

const Card2Importar = memo(function Card2Importar_Inner({kpis, setKpis, importStatus, setImportStatus}){
  const [modo, setModo] = useState('manual');
  const [textoJSON, setTextoJSON] = useState('');
  const [previewExcel, setPreviewExcel] = useState(null);

  const aplicarKpis = (k, formato, linhas) => {
    setKpis(prev => ({...prev, ...k}));
    setImportStatus({ok:true, formato, linhas: linhas||0, kpis:k, ts:Date.now()});
  };

  const handleJSON = () => {
    if (!textoJSON.trim()) {
      setImportStatus({ok:false, erro:'Cole o JSON antes de aplicar.'});
      return;
    }
    const r = importarJSONkpis(textoJSON);
    if (r.ok) aplicarKpis(r.kpis, 'JSON', textoJSON.split('\n').length);
    else setImportStatus({ok:false, erro:r.erro});
  };

  const handleExcel = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const r = importarExcelKpis(evt.target.result);
      if (r.ok) {
        aplicarKpis(r.kpis, 'Excel ('+file.name+')', r.preview?.length||0);
        setPreviewExcel(r.preview);
      } else {
        setImportStatus({ok:false, erro:r.erro});
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handlePDF = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportStatus({
      ok:false,
      erro:`Extração de PDF estruturado exige Backend Manta (PK_07 · 02.2). Arquivo recebido: ${file.name} · ${(file.size/1024).toFixed(1)} KB. Por ora: abra o PDF, copie os dados quantitativos e cole no modo Manual ou JSON.`
    });
  };

  const btnModo = (k, ic, l) => (
    <button onClick={()=>setModo(k)} style={{
      flex:1, padding:'12px 14px', fontSize:11, fontWeight:700,
      background: modo===k ? M.accent : '#fff',
      color: modo===k ? '#fff' : M.text,
      border:`1.5px solid ${modo===k ? M.accent : M.border}`,
      borderRadius:5, cursor:'pointer', fontFamily:F.body,
      display:'flex', flexDirection:'column', alignItems:'center', gap:4,
    }}>
      <span style={{fontSize:18}}>{ic}</span>
      <span>{l}</span>
    </button>
  );

  const inputStl = {width:'100%', padding:'7px 10px', border:`1px solid ${M.border}`, borderRadius:4, fontFamily:F.body, fontSize:11.5, color:M.text, outline:'none'};

  return (<div>
    <div style={{fontSize:11.5, color:M.muted, marginBottom:14}}>
      Suba um arquivo com os quantitativos do projeto. Os campos do card 3 serão preenchidos automaticamente.
    </div>

    {/* Botões de modo */}
    <div style={{display:'flex', gap:8, marginBottom:18}}>
      {btnModo('sharepoint','☁️', 'SharePoint Manta')}
      {btnModo('pdf',    '📄', 'PDF (PER/Edital)')}
      {btnModo('excel',  '📊', 'Excel/CSV')}
      {btnModo('json',   '📋', 'JSON')}
      {btnModo('manual', '✏️',  'Manual')}
    </div>

    {/* Conteúdo por modo */}
    <div style={{padding:'14px 16px', background:M.bgAlt, borderRadius:6, border:`1px solid ${M.borderSoft}`}}>
      {modo==='sharepoint' && (
        <div>
          <div style={{fontSize:11, color:M.text, marginBottom:10, lineHeight:1.5}}>
            <b>Híbrido fail-loud:</b> escolha um arquivo das pastas Manta favoritas, ou cole URL/path do SharePoint. Se Backend Manta estiver ON, baixa automaticamente. Se OFF, abre instruções de download manual.
          </div>
          <SharePointPicker
            onArquivoSelecionado={(info) => {
              // Conteúdo recebido (modo backend ON)
              const r = importarJSONkpisAvancado(info.conteudo);
              if (r.ok){
                aplicarKpis(r.kpis, 'SharePoint · '+info.nome+' (formato '+r.formato+')', r.n_kpis);
              } else {
                setImportStatus({ok:false, erro:r.erro+' · arquivo: '+info.nome});
              }
            }}
            onErro={(e) => {
              if (e.tipo === 'backend-off' || e.tipo === 'backend-erro'){
                // Mostrar instruções de download manual
                setImportStatus({
                  ok:false,
                  erro: `${e.msg}.\n\nINSTRUÇÕES MANUAIS:\n1. Abra o link: ${e.url}\n2. Baixe o arquivo${e.nome?' '+e.nome:''} para sua máquina\n3. Volte aqui e use o modo "JSON" para colar o conteúdo, ou "Excel/CSV" para arquivos planilhas\n\nQuando o Backend Manta entrar em produção, este passo será automático.`,
                });
              }
            }}
          />
          <CalloutM num="Q-05.2.0" tipo="info" label="Backend SharePoint (futuro)">
            Endpoint esperado: GET /api/sharepoint/health (probe) · GET /api/sharepoint/file?uri=... (download) · ver BACKEND_SHAREPOINT_API.md para spec completa.
          </CalloutM>
        </div>
      )}

      {modo==='pdf' && (
        <div>
          <div style={{fontSize:11, color:M.text, marginBottom:10, lineHeight:1.5}}>
            <b>Como funciona:</b> ao subir um PDF (PER, edital, projeto básico), o Backend Manta extrairia automaticamente extensão, OAEs, duplicação, marginais, etc. Em desenvolvimento.
          </div>
          <input type="file" accept=".pdf" onChange={handlePDF} style={{fontSize:11, fontFamily:F.body}}/>
          <CalloutM num="Q-05.2.1" tipo="warn" label="Limitação declarada (Cap 09)">
            Extração de PDF estruturado exige Backend Python (pdfplumber + camelot). Frontend JS não tem capacidade equivalente. Stub atual aceita o upload e devolve mensagem honesta. Como workaround, abra o PDF, copie os dados e cole no modo JSON ou preencha Manual.
          </CalloutM>
        </div>
      )}

      {modo==='excel' && (
        <div>
          <div style={{fontSize:11, color:M.text, marginBottom:8, lineHeight:1.5}}>
            <b>Formato esperado:</b> 2 colunas (Chave | Valor). Chaves reconhecidas: <code style={{fontFamily:F.mono, fontSize:10, background:'#fff', padding:'1px 4px'}}>extensao_km</code>, <code style={{fontFamily:F.mono, fontSize:10, background:'#fff', padding:'1px 4px'}}>duplicacao_km</code>, <code style={{fontFamily:F.mono, fontSize:10, background:'#fff', padding:'1px 4px'}}>oaes</code>, etc.
          </div>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleExcel} style={{fontSize:11, fontFamily:F.body, marginBottom:10}}/>
          {previewExcel && (
            <div style={{marginTop:10, maxHeight:200, overflowY:'auto', background:'#fff', border:`1px solid ${M.borderSoft}`, borderRadius:4}}>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:10, fontFamily:F.mono}}>
                <tbody>
                  {previewExcel.slice(0,15).map((row,i) => (
                    <tr key={i} style={{borderBottom:`1px solid ${M.borderSoft}`}}>
                      {row.slice(0,3).map((c,j) => (
                        <td key={j} style={{padding:'4px 8px', color:i===0?M.heading:M.text, fontWeight:i===0?700:400}}>{c==null?'—':String(c)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modo==='json' && (
        <div>
          <div style={{fontSize:11, color:M.text, marginBottom:8, lineHeight:1.5}}>
            <b>Formato:</b> objeto JSON com as chaves: <code style={{fontFamily:F.mono, fontSize:10, background:'#fff', padding:'1px 4px'}}>extKm, dupKm, margKm, fxKm, ciclKm, oaeQt, passQt, interQt</code>
          </div>
          <textarea value={textoJSON} onChange={e=>setTextoJSON(e.target.value)} rows={6}
            placeholder='{"extKm":120, "dupKm":40, "oaeQt":15, "passQt":3, "interQt":8, "ciclKm":2}'
            style={{...inputStl, fontFamily:F.mono, fontSize:11, resize:'vertical'}}/>
          <button onClick={handleJSON} style={{
            marginTop:10, padding:'7px 16px', fontSize:11, fontWeight:700, color:'#fff',
            background:M.accent, border:'none', borderRadius:4, cursor:'pointer', fontFamily:F.body,
          }}>Aplicar valores</button>
        </div>
      )}

      {modo==='manual' && (
        <div style={{fontSize:11.5, color:M.text, lineHeight:1.6}}>
          Preencha diretamente os KPIs no <b>card 3</b>. Use este modo quando não tiver arquivo para importar.
          <div style={{marginTop:10, padding:'8px 12px', background:'#fff', borderRadius:4, fontSize:10.5, color:M.muted, fontStyle:'italic'}}>
            💡 Dica: avance para o card 3 e comece preenchendo extensão (km) e número de OAEs · esses dois sozinhos já permitem similaridade aproximada.
          </div>
        </div>
      )}
    </div>

    {/* Status do import */}
    {importStatus && (
      <div style={{
        marginTop:14, padding:'12px 14px', borderRadius:5,
        background: importStatus.ok ? '#F0FDF4' : '#FEF3C7',
        borderLeft: `3px solid ${importStatus.ok ? M.green : M.yellow}`,
      }}>
        <div style={{fontSize:10.5, fontWeight:700, color: importStatus.ok ? M.green : M.yellow, letterSpacing:.5, textTransform:'uppercase', marginBottom:4}}>
          {importStatus.ok ? '✓ Importado com sucesso' : '⚠ Não importado'}
        </div>
        {importStatus.ok && (<div style={{fontSize:11.5, color:M.text, lineHeight:1.5}}>
          Formato: <b>{importStatus.formato}</b> · {importStatus.linhas} linhas processadas. Valores aplicados aos KPIs do card 3.
        </div>)}
        {importStatus.erro && (<div style={{fontSize:11, color:M.text, lineHeight:1.5}}>{importStatus.erro}</div>)}
      </div>
    )}
  </div>);
});

export { Card2Importar };
export default Card2Importar;
